import { User } from '@/types';
import styles from '../styles.module.css';
import { Text, Icon, IconType } from '@/ui';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Column } from '@/components/SettingsLayout';
import { useGetUsersQuery } from '@/store';
import { skipToken } from '@reduxjs/toolkit/query';
import { UserFilters } from '@/services/User/user.service';
import { usePaginationFilter } from '@/hooks/usePaginationFilter';
import { SkeletonFullWidthAndHeight } from '@/components/SkeletonLoader/SkeletonElements';
import ImageWithFallback from '@/ui/ImageWithFallBack/ImageWithFallBack';

function OrgUsers() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const openEditForm = (userId: string) => {
    navigate(`${pathname}?userId=${userId}&mode=editUser`);
  };
  const { filter } = usePaginationFilter<UserFilters>({
    limit: 100,
    offset: 0,
    isDeleted: false,
    companies: [id || ''],
    organizationId: id || '',
  });
  const addNewEmployee = () => {
    navigate(`${pathname}?mode=addUser`);
  };

  const { data, isLoading } = useGetUsersQuery(
    id ? { organizationId: id, filters: filter } : skipToken,
  );

  if (isLoading) {
    return (
      <div className={styles.usersGrid}>
        <SkeletonUserCard />
        <SkeletonUserCard />
        <SkeletonUserCard />
        <SkeletonUserCard />
        <SkeletonUserCard />
        <SkeletonUserCard />
        <SkeletonUserCard />
        <SkeletonUserCard />
      </div>
    );
  }

  return (
    <div className={styles.usersGrid}>
      {data?.data?.map((user) => {
        return <UserCard key={user._id} user={user} action={() => openEditForm(user._id)} />;
      })}
      <div className={`${styles.userCard} ${styles.addNew}`} onClick={addNewEmployee}>
        <Column fullHeight align='center' justify='justify-center'>
          <div className={styles.bluePlus}>
            <Icon type={IconType.Plus} width={20} />
          </div>
          <Text size='xs' weight='light-medium'>
            {t('Shared.add_new')}
          </Text>
        </Column>
      </div>
    </div>
  );
}

export default OrgUsers;

type Props = {
  user: User;
  action: () => void;
};
const UserCard = ({ user, action }: Props) => {
  const { t } = useTranslation();

  return (
    <div className={styles.userCard}>
      <button className={styles.editBtn} onClick={action}>
        <Icon type={IconType.Pencil_Edit} width={24} height={24} fill='transparent' />
      </button>
      <div className={styles.userAvatarContainer}>
        <ImageWithFallback
          src={user.image || ''}
          placeholderSrc='/male_placeholder.png'
          width={60}
          height={60}
        />

        <Text size='s' weight='medium'>
          {user.firstName} {user.lastName}
        </Text>
      </div>
      <div className={styles.userInfo}>
        <Text size='xs' weight='light-medium'>
          {t('Accounts.employee_email', {
            email: user.email,
          })}
        </Text>
      </div>
    </div>
  );
};

export const SkeletonUserCard = () => {
  return (
    <div className={styles.userCard}>
      <SkeletonFullWidthAndHeight />
    </div>
  );
};
